import React, { useContext, useEffect, useState } from 'react';

import {
    BasePlan,
    GetBasePlansInput,
    GetBasePlansOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption, SelectOptionGroup } from '@amzn/meridian/select';

import { contractConfigurationClient } from 'src/api/client';
import { ALPHANUMERIC_CAPITALIZED_UNDERSCORES_ONLY, EXACTLY_10_CHAR } from 'src/constants/errorMessages';
import { AppContext } from 'src/contexts/appContext';
import { IBasePlan } from 'src/types/IBasePlanLaunchDate';
import { validateAlphanumericCapitalizedAndUnderscore, validateExactLength } from 'src/util/validationsUtil';

interface IAddNewBasePlanIdProps {
    basePlanId: number | undefined;
    onChange: (basePlan: IBasePlan) => void;
    validateField: () => void;
    errorMessage: string;
    disabled?: boolean;
}

export const BasePlanIdSelect = ({
    basePlanId,
    onChange,
    validateField,
    errorMessage,
    disabled = false
}: IAddNewBasePlanIdProps) => {
    const [newBasePlanName, setNewBasePlanName] = useState('');
    const [basePlanNameOptions, setBasePlanNameOptions] = useState<BasePlan[]>([]);
    const [isBasePlanNameModalOpen, setIsBasePlanNameModalOpen] = useState(false);
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});
    const { setTopAlertMsg } = React.useContext(AppContext);
    const newBasePlanOption = {
        value: 0.1,
        label: 'Add a new base plan ID...'
    };
    const newBasePlanId = 0;

    // Open modal when "Add a new base plan ID..." option selected
    useEffect(() => {
        if (basePlanId === newBasePlanOption.value) {
            setIsBasePlanNameModalOpen(true);
        }
    }, [basePlanId]);

    // Get base plans on component initialization
    useEffect(() => {
        contractConfigurationClient.getBasePlans(GetBasePlansInput.clone({})).subscribe(
            (response: GetBasePlansOutput) => {
                const sortedBasePlans = response.basePlans?.length ? sortBasePlans(response.basePlans) : [];
                setBasePlanNameOptions(sortedBasePlans);
            },
            (error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                console.error('getBasePlans unexpected error', error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            }
        );
    }, []);

    const sortBasePlans = (basePlans: BasePlan[]) => {
        return basePlans.sort((a, b) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const nameA = a.planName!.toUpperCase();
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const nameB = b.planName!.toUpperCase();
            if (nameA < nameB) {
                return -1;
            }
            if (nameA > nameB) {
                return 1;
            }
            return 0;
        });
    };

    const handleOnChange = (selectedBasePlanId: number) => {
        const basePlanName =
            selectedBasePlanId === newBasePlanOption.value
                ? newBasePlanOption.label
                : basePlanNameOptions.find((basePlan) => basePlan.basePlanId === selectedBasePlanId)?.planName;
        const basePlan: IBasePlan = {
            basePlanId: selectedBasePlanId,
            basePlanName
        };
        onChange(basePlan);
    };

    const addNewBasePlanName = () => {
        const basePlan: IBasePlan = {
            basePlanId: newBasePlanId,
            basePlanName: newBasePlanName
        };
        onChange(basePlan);
        setIsBasePlanNameModalOpen(false);
    };

    const handleCancelAddNewBasePlanName = () => {
        setIsBasePlanNameModalOpen(false);
        setNewBasePlanName('');
        onChange({});
    };

    const onBlurField = (value: string) => {
        let errorMessage = '';

        if (!validateExactLength(value, 10)) {
            errorMessage = EXACTLY_10_CHAR;
        } else if (!validateAlphanumericCapitalizedAndUnderscore(value)) {
            errorMessage = ALPHANUMERIC_CAPITALIZED_UNDERSCORES_ONLY;
        }

        setErrorMessages((prevState) => {
            const newState = { ...prevState };
            if (errorMessage === '') {
                delete newState['newBasePlanName'];
            } else {
                newState['newBasePlanName'] = errorMessage;
            }
            return newState;
        });
    };

    return (
        <>
            <Select
                label="Base plan ID *"
                value={basePlanId}
                onChange={(value) => handleOnChange(value)}
                onBlur={() => validateField()}
                error={!!errorMessage}
                errorMessage={errorMessage}
                disabled={disabled}
                size="xlarge"
            >
                <SelectOptionGroup label="">
                    <SelectOption value={newBasePlanOption.value} label={newBasePlanOption.label} />
                </SelectOptionGroup>
                {newBasePlanName && <SelectOption value={newBasePlanId} label={newBasePlanName} />}
                {basePlanNameOptions.map((basePlan) => (
                    <SelectOption
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        value={basePlan.basePlanId!}
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        label={basePlan.planName!}
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        key={basePlan.basePlanId!}
                    />
                ))}
            </Select>

            <Modal
                title="Add new base plan ID"
                open={isBasePlanNameModalOpen}
                onClose={handleCancelAddNewBasePlanName}
                scrollContainer="viewport"
                closeLabel="Close"
                aria-describedby="modal-description"
            >
                <Input
                    label="Base plan ID *"
                    value={newBasePlanName}
                    onBlur={() => onBlurField(newBasePlanName)}
                    error={!!errorMessages.newBasePlanName}
                    errorMessage={errorMessages.newBasePlanName}
                    onChange={(value) => setNewBasePlanName(value)}
                    size="xlarge"
                    width={400}
                />
                <ModalFooter>
                    <Row alignmentHorizontal="end" widths="fit">
                        <Button type="secondary" size="small" onClick={handleCancelAddNewBasePlanName}>
                            Cancel
                        </Button>
                        <Button type="primary" size="small" onClick={addNewBasePlanName}>
                            Add base plan ID
                        </Button>
                    </Row>
                </ModalFooter>
            </Modal>
        </>
    );
};
